// Shared helpers for the Denver package builder's option lists.

import { parseGroupSize } from './pricingModel';

// -------------------------------------------------------------------------
// Food categories shown in the builder
// -------------------------------------------------------------------------
// The catalog keeps its detailed categories; the builder groups the small
// ones so guests scan fewer accordions.

export const FOOD_DISPLAY_CATEGORY = {
  'Buffets & Plated Meals': 'Buffets, Plated Meals & Stations',
  'Food Stations & Specialty': 'Buffets, Plated Meals & Stations',
  'Snacks & Meeting Breaks': 'Snacks, Breaks & Beverages',
  'Non-Alcoholic Beverages': 'Snacks, Breaks & Beverages',
  'Bar & Alcohol Service': 'Bar, Staffing & Rentals',
  'Staffing, Delivery & Rentals': 'Bar, Staffing & Rentals',
};

export const FOOD_DISPLAY_ORDER = [
  'Breakfast & Brunch',
  'Boxed Meals & Lunch',
  'Buffets, Plated Meals & Stations',
  'Appetizers & Receptions',
  'Snacks, Breaks & Beverages',
  'Desserts & Sweets',
  'Bar, Staffing & Rentals',
];

export function foodDisplayCategory(category) {
  return FOOD_DISPLAY_CATEGORY[category] || category || 'Other';
}

// -------------------------------------------------------------------------
// Price source tags
// -------------------------------------------------------------------------

export const PRICE_SOURCE_LABELS = {
  published: 'Published rate',
  estimate: 'Teamward estimate',
  teamward: 'Teamward price',
  free: 'No cost',
};

// Maps a pricing confidence value to the tag shown next to every price.
export function priceSourceFor(confidence, { free = false } = {}) {
  if (free) return 'free';
  if (confidence === 'published' || confidence === 'confirmed') return 'published';
  if (confidence === 'teamward') return 'teamward';
  return 'estimate';
}

// -------------------------------------------------------------------------
// "Popular for groups this size" ranking
// -------------------------------------------------------------------------
// There is no booking history in the catalog, so this ranks options by how
// well their published group-size range suits the headcount, then by the
// catalog's curated order. An option sized for ~25 people ranks above one
// that technically allows 25 but is built for 300.

const OPEN_ENDED_MAX = 500;

export function groupSizeFitBand(range, headcount) {
  if (!range || !headcount) return 1;

  const min = Math.max(1, range.min || 1);
  const max = Number.isFinite(range.max) ? range.max : OPEN_ENDED_MAX;

  if (headcount < min || headcount > Math.max(max, min)) return 3;

  const midpoint = Math.sqrt(min * Math.max(max, min));
  const ratio = headcount / midpoint;

  if (ratio >= 0.5 && ratio <= 2) return 0;
  if (ratio >= 0.25 && ratio <= 4) return 1;
  return 2;
}

export function vendorSizeFitBand(vendor, headcount) {
  return groupSizeFitBand(parseGroupSize(vendor && vendor.groupSize), headcount);
}

export function foodSizeFitBand(option, headcount) {
  if (!option) return 1;
  return groupSizeFitBand(
    {
      min: option.minHeadcount || 1,
      max: option.maxHeadcount || Infinity,
    },
    headcount
  );
}

export const SORT_MODES = [
  { id: 'popular', label: 'Popular for groups this size' },
  { id: 'budget', label: 'Best fit for my budget' },
  { id: 'price', label: 'Lowest price first' },
];

const BUDGET_RANK = { within: 0, overlap: 1, over: 2 };

// Sorts without mutating. `fitBand(option)` ranks group-size fit, `budgetFit`
// returns within/overlap/over, `costLow(option)` returns a comparable cost and
// `boost(option)` returns true for curated favorites.
export function sortOptions(
  options,
  mode,
  { fitBand = () => 1, budgetFit = () => null, costLow = () => null, boost = () => false } = {}
) {
  const cost = (option) => {
    const value = costLow(option);
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
  };

  return options
    .map((option, index) => ({ option, index }))
    .sort((a, b) => {
      if (mode === 'price') {
        return cost(a.option) - cost(b.option) || a.index - b.index;
      }

      if (mode === 'budget') {
        return (
          (BUDGET_RANK[budgetFit(a.option)] ?? 3) - (BUDGET_RANK[budgetFit(b.option)] ?? 3) ||
          cost(a.option) - cost(b.option) ||
          a.index - b.index
        );
      }

      return (
        fitBand(a.option) - fitBand(b.option) ||
        Number(boost(b.option)) - Number(boost(a.option)) ||
        a.index - b.index
      );
    })
    .map(({ option }) => option);
}

// -------------------------------------------------------------------------
// Vendor count for the coordination prompt
// -------------------------------------------------------------------------

const NON_VENDOR_VENUE_IDS = new Set(['your-office', 'activity-location']);

// Counts distinct outside businesses in the package: one rented venue (extra
// venues are side-by-side comparisons, not extra bookings), each experience
// provider and each caterer.
export function countPackageVendors({ venueIds = [], vendorIds = [], foodOptions = [] }) {
  const venues = venueIds.some((id) => !NON_VENDOR_VENUE_IDS.has(id)) ? 1 : 0;
  const caterers = new Set(foodOptions.map((option) => option.vendor || option.id)).size;
  return venues + new Set(vendorIds).size + caterers;
}
